import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { storageApi } from '../api';
import { RegionShape, isShape, shapeLabelStyle, zoneStyle } from './RegionShape';
import { ZoneProducts } from './ZoneProducts';
import './ShelfPreview.css';

// Units are fetched once per panel session; call clearShelfCache() when reopening.
const unitCache = new Map();

export function clearShelfCache() {
  unitCache.clear();
}

function loadUnit(unitId) {
  if (!unitCache.has(unitId)) {
    unitCache.set(unitId, storageApi.getUnit(unitId).catch(error => {
      unitCache.delete(unitId);
      throw error;
    }));
  }
  return unitCache.get(unitId);
}

// A shelf photo with one bin marked with the highlighter, like the picking view
export function ShelfPreview({ unitId, regionId, sku }) {
  const [unit, setUnit] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setUnit(null);
    setError('');
    loadUnit(unitId)
      .then(data => active && setUnit(data))
      .catch(err => active && setError(err.message));
    return () => {
      active = false;
    };
  }, [unitId]);

  if (error) {
    return <p className="shelf-preview-error">Couldn't load this shelf: {error}</p>;
  }

  if (!unit) {
    return <div className="shelf-preview-loading skeleton" aria-label="Loading shelf photo" />;
  }

  const region = unit.regions.find(r => r.id === regionId);

  return (
    <figure className="shelf-preview">
      <div className="shelf-preview-image">
        <img src={unit.image_url} alt={unit.name} />
        {region && (
          <div
            className={`shelf-preview-region ${isShape(region) ? 'is-shape' : ''}`}
            style={{
              left: `${region.x}%`,
              top: `${region.y}%`,
              width: `${region.width}%`,
              height: `${region.height}%`,
              ...zoneStyle(region.color),
            }}
          >
            <RegionShape region={region} />
            <span className="shelf-preview-label" style={shapeLabelStyle(region)}>{region.label || sku}</span>
          </div>
        )}
      </div>
      <figcaption className="shelf-preview-caption">
        <span>
          {sku && <><span className="mono">{sku}</span> is in </>}
          {region?.label && <><span className="bin-tag">{region.label}</span> on </>}
          <strong>{unit.name}</strong>
        </span>
        <Link to={`/storage/${unit.id}`} className="shelf-preview-link">
          Open shelf <ArrowUpRight size={14} aria-hidden="true" />
        </Link>
      </figcaption>
      {region?.skus?.length > 0 && (
        <div className="shelf-preview-products">
          <h3>In this bin</h3>
          <ZoneProducts skus={region.skus} products={unit.products} />
        </div>
      )}
    </figure>
  );
}
